import type { components } from "@/api-schema";
import { PathsV1ModelGenerationSearchPostParametersQueryExtend } from "@/api-schema-enums";
import { useModelGenerationSearchQuery } from "@/common/api/model-generation/use-model-generation-search.query";
import { getModelGenerationDatId } from "@/common/helpers/get-model-generation-dat-id";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

type QueryBody = components["schemas"]["ModelGenerationSearchBody"];

const FILTER_PAGE_SIZE = 50;

export const MODEL_GENERATION_DAT_ID_EMPTY = "IS_NULL";

function useModelGenerationDatIdFilterData(body: QueryBody): {
  data: FilterData;
  pagination: FilterPaginationState;
} {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: modelGenerations,
    isPending: isLoading,
    error,
  } = useModelGenerationSearchQuery({
    options: {
      parameters: {
        extend: [PathsV1ModelGenerationSearchPostParametersQueryExtend.ModelGenerationIdentifiers],
        pageSize,
        search: debouncedSearchTerm || undefined,
        searchField: ["modelGenerationIdentifiers.identifier"],
      },
      body,
    },
  });

  const options = [
    { id: MODEL_GENERATION_DAT_ID_EMPTY, name: "common:Empty", className: "italic" },
    ...(modelGenerations?.data
      .map((modelGeneration) => getModelGenerationDatId(modelGeneration.modelGenerationIdentifiers))
      .filter((datId): datId is string => !!datId)
      .map((datId) => ({ id: datId, name: datId }))
      .filter((option, index, array) => array.findIndex((item) => item.id === option.id) === index) ?? []),
  ];
  const loadedCount = modelGenerations?.data.length ?? 0;
  const total = modelGenerations?.meta.total ?? 0;

  return {
    data: { options, isLoading, error },
    pagination: createPaginationState(total, loadedCount, isLoading),
  };
}

export class ModelGenerationDatIdFilter extends Filter<QueryBody, unknown> {
  readonly id = "identifier";
  readonly labelSingular = "masterdata:DATID";
  readonly labelPlural = "masterdata:DATID";
  readonly placeholder = "masterdata:DATID";
  readonly searchPlaceholder = "masterdata:DATID";
  readonly emptyText = "common:NoOptionsFound";

  constructor({ body = {} }: { body?: Partial<QueryBody> } = {}) {
    super();
    this.body = body as QueryBody;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <ModelGenerationDatIdFilterComponent filter={this} {...props} />;
  }
}

function ModelGenerationDatIdFilterComponent({
  filter,
  ...props
}: { filter: ModelGenerationDatIdFilter } & BaseFilterProps) {
  const { data, pagination } = useModelGenerationDatIdFilterData(filter.body);
  const filterWithData = Object.create(filter);
  filterWithData.getData = () => ({
    ...data,
    options: [
      ...data.options,
      ...props.selectedValues
        .filter((datId) => !data.options.some((option) => option.id === datId))
        .map((datId) => ({ id: datId, name: datId })),
    ],
  });

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
